import { FC, ReactNode, useEffect, useMemo, useState } from 'react';

import {
  Box,
  Button,
  MenuItem,
  Stack,
  TextField,
  Typography,
  styled,
  useTheme,
} from '@mui/material';
import moment from 'moment';

import { AddressApi } from '@/api';
import { IAddressPreplanResponse } from '@/api/types';
import { IcArrowLeft, IcEventList } from '@/assets/images';
import { AppModal, FileGrid } from '@/components';
import { DATE_FORMAT } from '@/config';
import { useCFSContext } from '@/hooks';
import { IFile } from '@/models';
import { getAddressLocation } from '@/services';
import { getFormFields } from '@/services/service.formfieldLoader';
import { colors } from '@/theme/variables';
import { formatPersonName } from '@/utils';

interface AddressPreplanModalProps {
  addressId?: string;
  agencyIds?: string[];
  hideAgencySelect?: boolean;
  renderTrigger?: (onOpen: () => void) => ReactNode;
  mode?: 'modal' | 'inline';
  onBack?: () => void;
  preplanData?: IAddressPreplanResponse | null;
  addressLabelOverride?: string;
}

const PreplanModalRoot = styled(AppModal)(() => ({
  '& .modal-content': {
    width: 600,
    maxWidth: '90vw',
    maxHeight: '80vh',
    overflow: 'hidden',
  },
}));

const AddressPreplanModal: FC<AddressPreplanModalProps> = ({
  addressId,
  agencyIds,
  hideAgencySelect = false,
  renderTrigger,
  mode = 'modal',
  onBack,
  preplanData: preplanDataOverride,
  addressLabelOverride,
}) => {
  const { cfs } = useCFSContext();
  const muiTheme = useTheme();
  const [openModal, setOpenModal] = useState(false);
  const [selectedAgency, setSelectedAgency] = useState<string | null>(null);
  const [preplanData, setPreplanData] =
    useState<IAddressPreplanResponse | null>(null);

  const effectiveAddressId = addressId || cfs?.addressInfo?.address?._id;
  const prePlanFields = (getFormFields() || {}) as Record<string, any[]>;

  const addressLabel = useMemo(() => {
    return (
      addressLabelOverride ||
      getAddressLocation(cfs?.addressInfo?.address) ||
      '—'
    );
  }, [addressLabelOverride, cfs?.addressInfo?.address]);

  const assignedAgencies =
    agencyIds ?? (cfs?.assignedAgencies ?? []).map(String);
  useEffect(() => {
    if (preplanDataOverride) return;
    if (!effectiveAddressId || assignedAgencies.length === 0) {
      setPreplanData(null);
      setSelectedAgency(null);
      return;
    }
    AddressApi.getPreplan(effectiveAddressId, assignedAgencies)
      .then((res) => {
        setPreplanData(res.data);
        setSelectedAgency(
          res.data.prePlans?.[0]?.agencyId
            ? String(res.data.prePlans[0].agencyId)
            : null,
        );
      })
      .catch(() => {
        setPreplanData(null);
        setSelectedAgency(null);
      });
  }, [preplanDataOverride, effectiveAddressId, assignedAgencies.join(',')]);

  useEffect(() => {
    if (!preplanDataOverride) return;
    const firstPlanAgencyId = preplanDataOverride.prePlans?.[0]?.agencyId;
    setSelectedAgency(firstPlanAgencyId ? String(firstPlanAgencyId) : null);
  }, [preplanDataOverride]);

  const resolvedPreplanData = preplanDataOverride ?? preplanData;
  const prePlans = resolvedPreplanData?.prePlans ?? [];
  const agenciesMap = resolvedPreplanData?.agencies ?? {};
  const agencyIdsWithPlans = Array.from(
    new Set(prePlans.map((plan) => String(plan.agencyId)).filter(Boolean)),
  );
  const selectedAgencyPlan = useMemo(
    () => prePlans.find((plan) => String(plan.agencyId) === selectedAgency),
    [prePlans, selectedAgency],
  );

  if (!effectiveAddressId || prePlans.length === 0) return null;
  const selectedPrePlanOption = selectedAgencyPlan?.prePlanOption;
  const fieldset =
    selectedPrePlanOption && prePlanFields[selectedPrePlanOption]
      ? prePlanFields[selectedPrePlanOption]
      : [];

  const content = (
    <Stack
      spacing={2}
      sx={{
        minWidth: mode === 'inline' ? 'auto' : 520,
        width: mode === 'inline' ? '100%' : 'auto',
        flex: mode === 'inline' ? 1 : 'initial',
      }}
    >
      {mode === 'inline' ? (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            pb: 1,
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          }}
        >
          <Button
            variant="text"
            onClick={onBack}
            sx={{
              textTransform: 'none',
              color: 'text.primary',
              fontSize: '1.25rem',
              fontWeight: 600,
            }}
            startIcon={<IcArrowLeft width={8} height={14} />}
          >
            Back
          </Button>
          <Typography variant="h6">Pre-Plan checklist</Typography>
          <Box sx={{ width: 60 }} />
        </Stack>
      ) : null}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={2}
      >
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}
        >
          {addressLabel}
        </Typography>
        {!hideAgencySelect && (
          <TextField
            select
            size="small"
            value={selectedAgency ?? ''}
            onChange={(event) => setSelectedAgency(event.target.value)}
            sx={{ minWidth: 200 }}
            SelectProps={{
              displayEmpty: true,
              readOnly: agencyIdsWithPlans.length <= 1,
            }}
            disabled={agencyIdsWithPlans.length <= 1}
          >
            {agencyIdsWithPlans.map((agencyId) => {
              const agencyName = agenciesMap[agencyId]?.name;
              return (
                <MenuItem key={agencyId} value={agencyId}>
                  {agencyName || agencyId}
                </MenuItem>
              );
            })}
          </TextField>
        )}
      </Stack>
      <Box
        sx={{ borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}
      />
      <Stack
        spacing={1.5}
        sx={
          mode === 'inline'
            ? { flex: 1, overflowY: 'auto' }
            : { maxHeight: 420, overflowY: 'auto' }
        }
      >
        {fieldset.map((field: any) => {
          const fieldKey = field.name?.split('.').pop() ?? '';
          const rawValue =
            fieldKey && selectedAgencyPlan
              ? (selectedAgencyPlan as Record<string, unknown>)[fieldKey]
              : '';
          let displayValue = rawValue;
          if (field.type === 'select' && Array.isArray(field.items)) {
            displayValue =
              field.items.find((item: any) => item.value === rawValue)?.label ??
              rawValue;
          }
          if (field.type === 'select' && field.items === 'getAgencyEmployees') {
            const submittedByUser = rawValue as {
              firstName?: string;
              lastName?: string;
            } | null;
            displayValue = formatPersonName(submittedByUser) || 'N/A';
          }
          if (typeof rawValue === 'boolean') {
            displayValue = rawValue ? 'Yes' : 'No';
          }
          if (field.type === 'date') {
            displayValue = rawValue ? moment(rawValue).format(DATE_FORMAT) : '';
          }
          const displayValueText =
            displayValue === null || displayValue === undefined
              ? ''
              : String(displayValue);
          return (
            <Box key={field.name}>
              <Typography variant="caption" color="text.secondary">
                {field.label}
              </Typography>
              {field.type === 'file' ? (
                Array.isArray(rawValue) && rawValue.length > 0 ? (
                  <Box sx={{ mt: 1 }}>
                    <FileGrid files={rawValue as IFile[]} cols={2} />
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.primary">
                    /
                  </Typography>
                )
              ) : (
                <Typography
                  variant="body2"
                  color="text.primary"
                  sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                >
                  {displayValueText || '/'}
                </Typography>
              )}
            </Box>
          );
        })}
      </Stack>
    </Stack>
  );

  if (mode === 'inline') {
    return <Box sx={{ flex: 1, px: 2, pt: 1, pb: 2 }}>{content}</Box>;
  }

  return (
    <Box
      className="address-action"
      sx={{ display: 'inline-flex', alignItems: 'center' }}
    >
      {renderTrigger ? (
        renderTrigger(() => setOpenModal(true))
      ) : (
        <Button
          color="primary"
          onClick={() => setOpenModal(true)}
          sx={{
            height: '36px',
            minWidth: '36px',
            padding: 0,
            borderRadius: '8px',
            color:
              muiTheme.palette.mode === 'dark'
                ? muiTheme.palette.text.primary
                : colors.grey[100],
            '&:hover': {
              backgroundColor:
                muiTheme.palette.mode === 'dark' ? '#223640' : '#F5F7F7',
            },
          }}
          aria-label="Pre-Plan checklist"
        >
          <Box display="flex" alignItems="center" justifyContent="center">
            <Box
              sx={{
                backgroundColor:
                  muiTheme.palette.mode === 'dark'
                    ? muiTheme.palette.background.formSubHeader
                    : colors.grey[20],
                color:
                  muiTheme.palette.mode === 'dark'
                    ? muiTheme.palette.text.primary
                    : colors.grey[100],
                borderRadius: '6px',
                padding: '0 6px',
                width: '44px',
                minWidth: '44px',
                height: '24px',
                boxSizing: 'border-box',
                textAlign: 'center',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'row',
                gap: '4px',
              }}
            >
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <IcEventList width={16} height={16} />
              </Box>
            </Box>
          </Box>
        </Button>
      )}
      <PreplanModalRoot
        open={openModal}
        title="Pre-Plan checklist"
        onClose={() => setOpenModal(false)}
      >
        {content}
      </PreplanModalRoot>
    </Box>
  );
};

export default AddressPreplanModal;
